import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import WorkDetailPage from '../../../components/work/WorkDetailPage';
import WorkFilePanel from '../../../components/work/shared/WorkFilePanel';
import WorkCheckbox from '../../../components/work/WorkCheckbox';
import WorkManageEntityModal from '../../../components/work/WorkManageEntityModal';
import WorkPriceInput from '../../../components/work/WorkPriceInput';
import WorkColorPickerField from '../../../components/work/WorkColorPickerField';
import ProjectOverviewTab from '../../../components/work/projects/ProjectOverviewTab';
import PersianDatePicker from '../../../components/PersianDatePicker';
import {
  projectsApi,
  projectFilesApi,
  projectMembersApi,
  projectMilestonesApi,
  discussionsApi,
  discussionRepliesApi,
  discussionFilesApi,
  discussionCategoriesApi,
  projectNotesApi,
  projectRatingsApi,
} from '../../../utils/workApi';
import { show422Error, ticketsApi, financeSettingsApi, ordersApi, expensesApi } from '../../../utils/api';
import { PANEL_PRIMARY_BTN_CLASS } from '../../../constants/ui';
import {
  buildDiscussionPayload,
  buildDiscussionReplyPayload,
  buildDiscussionCategoryPayload,
  buildProjectMemberPayload,
  buildProjectMemberGroupPayload,
  buildProjectMilestonePayload,
  buildProjectNotePayload,
  buildProjectRatingPayload,
} from '../../../utils/workPayloadBuilders';
import { getProjectFromTabResponse } from '../../../utils/workResponseUtils';
import { formatWorkPriceDisplay } from '../../../utils/workPriceUtils';
import { toJalali } from '../../../utils/dateUtils';
import { workActionsColumn } from '../../../utils/workTableHelpers';
import { confirmWorkDelete } from '../../../utils/workConfirmModal';
import { stripHtml } from '../../../utils/stripHtml';
import {
  translateProjectActivity,
  getProjectActivityKind,
  PROJECT_ACTIVITY_EMPTY,
} from '../../../utils/projectActivityUtils';
import {
  Button,
  Card,
  Tabs,
  Tag,
  Progress,
  Table,
  message,
  Input,
  Modal,
  Form,
  Rate,
  List,
  Popconfirm,
  Select,
  Segmented,
  Spin,
  Row,
  Col,
  Space,
  Avatar,
  Tooltip,
  Empty,
  Descriptions,
} from 'antd';
import {
  ProjectOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  SafetyCertificateFilled,
  CheckOutlined,
  EyeOutlined,
  FileExcelOutlined,
  PaperClipOutlined,
  CheckSquareOutlined,
  FlagOutlined,
  UserAddOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import PermissionGate from '../../../components/PermissionGate';
import { WorkProjectTabSkeleton } from '../../../components/SkeletonLoaders';
import { useDetailReturnRefs } from '../../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../../hooks/useEntityBreadcrumb';

const { TextArea } = Input;
const { Option } = Select;

const formatWorkDate = (d) => {
  if (!d) return '—';
  return toJalali(d) || d;
};

const formatWorkDateTime = (d) => {
  if (!d) return '—';
  const withTime = toJalali(d, 'jYYYY/jMM/jDD HH:mm');
  if (withTime && withTime !== 'Invalid date') return withTime;
  return toJalali(d) || d;
};

const TICKET_STATUS_FA = {
  open: 'باز',
  pending: 'در انتظار',
  resolved: 'حل‌شده',
  closed: 'بسته',
};
const TICKET_PRIORITY_FA = {
  low: 'پایین',
  medium: 'متوسط',
  high: 'بالا',
  urgent: 'فوری',
};

const activityKindIcon = (kind) => {
  const common = { className: 'text-base' };
  switch (kind) {
    case 'file':
      return <PaperClipOutlined {...common} className="text-base text-sky-600" />;
    case 'task':
      return <CheckSquareOutlined {...common} className="text-base text-emerald-600" />;
    case 'milestone':
      return <FlagOutlined {...common} className="text-base text-amber-600" />;
    case 'member':
      return <UserAddOutlined {...common} className="text-base text-violet-600" />;
    case 'timer':
      return <ClockCircleOutlined {...common} className="text-base text-orange-600" />;
    case 'update':
      return <SyncOutlined {...common} className="text-base text-teal-600" />;
    case 'project':
      return <ProjectOutlined {...common} className="text-base text-blue-600" />;
    default:
      return <HistoryOutlined {...common} className="text-base text-gray-500" />;
  }
};

const formatCurrencyLabel = (c) => {
  if (!c) return '—';
  if (c.currency_name) return c.currency_name;
  const sym = c.currency_symbol || '';
  const code = c.currency_code || '';
  if (sym && code) return `${sym} (${code})`;
  return sym || code || String(c.id);
};

const MemberHourlyRateCell = ({ row, saving, onSave }) => {
  const [val, setVal] = useState(row.hourly_rate ?? 0);
  useEffect(() => {
    setVal(row.hourly_rate ?? 0);
  }, [row.hourly_rate, row.id]);
  return (
    <Space size={4} wrap style={{ fontFamily: 'CustomFont' }}>
      <WorkPriceInput
        size="small"
        value={val}
        onChange={setVal}
        style={{ width: 130, minWidth: 100 }}
      />
      <Button
        type="primary"
        size="small"
        icon={<CheckOutlined />}
        loading={saving}
        className={PANEL_PRIMARY_BTN_CLASS}
        onClick={() => onSave(Number(val))}
      />
    </Space>
  );
};

const TAB_GROUPS = {
  main: ['overview', 'members', 'files', 'milestones', 'discussion'],
  finance: ['invoices', 'estimates', 'orders', 'expenses', 'payments'],
  other: ['tickets', 'activity', 'notes', 'rating'],
};

const TAB_LABELS = {
  overview: 'خلاصه',
  members: 'اعضا',
  files: 'فایل',
  milestones: 'مراحل',
  discussion: 'بحث',
  invoices: 'فاکتور',
  estimates: 'برآورد',
  orders: 'سفارش',
  expenses: 'هزینه',
  payments: 'پرداخت',
  tickets: 'تیکت',
  activity: 'فعالیت',
  notes: 'یادداشت',
  rating: 'امتیاز',
};

const GROUP_LABELS = {
  main: 'اصلی',
  finance: 'مالی',
  other: 'سایر',
};

const label = (t) => <span style={{ fontFamily: 'CustomFont' }}>{t}</span>;

const cellFont = (v) => (
  <span style={{ fontFamily: 'CustomFont' }}>{v ?? '—'}</span>
);

const cellDate = (v) => cellFont(formatWorkDate(v));

const getGroupForTab = (tabKey) => {
  if (TAB_GROUPS.finance.includes(tabKey)) return 'finance';
  if (TAB_GROUPS.other.includes(tabKey)) return 'other';
  return 'main';
};

const TabToolbar = ({ children }) => (
  <div className="flex flex-wrap justify-end gap-2 mb-4" style={{ fontFamily: 'CustomFont' }}>
    {children}
  </div>
);

const ViewProject = () => {
  const { id } = useParams();
  const projectId = Number(id);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const goBack = () =>
    navigate(returnToRef.current || '/work/projects', { state: buildGoBackState() });
  const tab = searchParams.get('tab') || 'overview';
  const tabGroup = getGroupForTab(tab);

  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [projectInfo, setProjectInfo] = useState(null);
  const [payload, setPayload] = useState(null);
  const { trail, navigateWithTrail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/work/projects',
    listLabel: 'پروژه‌ها',
    entityLabel: projectInfo?.project_name || (id ? `پروژه #${id}` : 'پروژه'),
  });

  const [noteModal, setNoteModal] = useState(false);
  const [pwdModal, setPwdModal] = useState(false);
  const [pwdNoteId, setPwdNoteId] = useState(null);
  const [viewNoteModal, setViewNoteModal] = useState(false);
  const [viewNoteLoading, setViewNoteLoading] = useState(false);
  const [viewNote, setViewNote] = useState(null);
  const [notesExporting, setNotesExporting] = useState(false);
  const [discModal, setDiscModal] = useState(false);
  const [discCatModal, setDiscCatModal] = useState(false);
  const [memberModal, setMemberModal] = useState(false);
  const [msModal, setMsModal] = useState(false);

  const [noteSaving, setNoteSaving] = useState(false);
  const [discSaving, setDiscSaving] = useState(false);
  const [memberSaving, setMemberSaving] = useState(false);
  const [msSaving, setMsSaving] = useState(false);
  const [ratingSaving, setRatingSaving] = useState(false);

  const [memberModalLoading, setMemberModalLoading] = useState(false);
  const [msModalLoading, setMsModalLoading] = useState(false);
  const [discModalLoading, setDiscModalLoading] = useState(false);
  const [noteModalLoading, setNoteModalLoading] = useState(false);
  const [pwdChecking, setPwdChecking] = useState(false);
  const [ratingDeleting, setRatingDeleting] = useState(false);
  const [memberDeletingId, setMemberDeletingId] = useState(null);
  const [msDeletingId, setMsDeletingId] = useState(null);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [noteEditLoadingId, setNoteEditLoadingId] = useState(null);
  const [discDeletingId, setDiscDeletingId] = useState(null);
  const [bestAnswerLoadingId, setBestAnswerLoadingId] = useState(null);
  const [replyDeletingId, setReplyDeletingId] = useState(null);
  const [hourlyRateSavingId, setHourlyRateSavingId] = useState(null);
  const [adminAssigningUserId, setAdminAssigningUserId] = useState(null);
  const [employeeDirectory, setEmployeeDirectory] = useState({});
  const [editNoteId, setEditNoteId] = useState(null);
  const [editMsId, setEditMsId] = useState(null);
  const [msStatusUpdatingId, setMsStatusUpdatingId] = useState(null);
  const [ratingEditing, setRatingEditing] = useState(false);

  const [discCategories, setDiscCategories] = useState([]);
  const [noteEmployees, setNoteEmployees] = useState([]);
  const [memberOpts, setMemberOpts] = useState({ employees: [], departments: [] });
  const [msMeta, setMsMeta] = useState({ currencies: [] });

  const [selectedDiscussionId, setSelectedDiscussionId] = useState(null);
  const [discussionDetail, setDiscussionDetail] = useState(null);
  const [discussionDetailLoading, setDiscussionDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  const [noteForm] = Form.useForm();
  const [pwdForm] = Form.useForm();
  const [discForm] = Form.useForm();
  const [memberForm] = Form.useForm();
  const [msForm] = Form.useForm();
  const [ratingForm] = Form.useForm();

  const noteType = Form.useWatch('type', noteForm);
  const existingRating = payload?.rating;

  const userNameById = useMemo(() => {
    const map = {};
    (projectInfo?.members || []).forEach((m) => {
      if (m.user?.id) map[m.user.id] = m.user.name;
    });
    (payload?.members || []).forEach((m) => {
      if (m.user?.id) map[m.user.id] = m.user.name;
    });
    noteEmployees.forEach((e) => {
      if (e.id) map[e.id] = e.name;
    });
    memberOpts.employees.forEach((e) => {
      if (e.id) map[e.id] = e.name;
    });
    Object.entries(employeeDirectory).forEach(([uid, name]) => {
      if (uid && name) map[uid] = name;
    });
    return map;
  }, [projectInfo, payload, noteEmployees, memberOpts.employees, employeeDirectory]);

  const resolveUserName = (userId, fallbackUser) => {
    const uid = userId ?? fallbackUser?.id;
    return fallbackUser?.name || userNameById[uid] || userNameById[Number(uid)] || (uid ? `کاربر #${uid}` : '—');
  };

  const activeGroupTabs = TAB_GROUPS[tabGroup] || TAB_GROUPS.main;

  const loadProjectInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await projectsApi.getById(id);
      const project = getProjectFromTabResponse(res) || res?.project;
      setProjectInfo(project);
    } catch {
      message.error('خطا در دریافت پروژه');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const reload = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) setRefreshing(true);
      else setTabLoading(true);
      try {
        const tabParam = tab === 'overview' ? 'overview' : tab;
        const res = await projectsApi.getById(id, tabParam);
        setPayload(res);
        if (tab === 'discussion') {
          const list = res?.discussion || res?.discussions || [];
          setSelectedDiscussionId((prev) => {
            if (!list.length) return null;
            if (prev && list.some((d) => d.id === prev)) return prev;
            return list[0]?.id ?? null;
          });
          if (!list.length) setDiscussionDetail(null);
        }
      } catch {
        message.error('خطا در بارگذاری داده‌ها');
      } finally {
        if (silent) setRefreshing(false);
        else setTabLoading(false);
      }
    },
    [id, tab],
  );

  useEffect(() => {
    loadProjectInfo();
  }, [loadProjectInfo]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await projectsApi.getEditData(id);
        if (cancelled) return;
        const map = {};
        (res?.employees || []).forEach((e) => {
          if (e.id) map[e.id] = e.name;
        });
        (res?.project?.members || []).forEach((m) => {
          if (m.user?.id) map[m.user.id] = m.user.name;
        });
        setEmployeeDirectory(map);
      } catch {
        /* optional lookup */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (tab !== 'discussion' || !selectedDiscussionId) return;
    let cancelled = false;
    const loadDetail = async () => {
      setDiscussionDetailLoading(true);
      try {
        const res = await discussionsApi.getById(selectedDiscussionId);
        if (!cancelled) {
          setDiscussionDetail(res?.discussion || res);
        }
      } catch {
        if (!cancelled) message.error('خطا در بارگذاری بحث');
      } finally {
        if (!cancelled) setDiscussionDetailLoading(false);
      }
    };
    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [tab, selectedDiscussionId]);

  useEffect(() => {
    if (tab !== 'rating') return;
    const r = payload?.rating;
    if (r) {
      ratingForm.setFieldsValue({ rating: Number(r.rating), comment: r.comment });
      setRatingEditing(false);
    } else {
      ratingForm.resetFields();
      setRatingEditing(true);
    }
  }, [tab, payload?.rating, ratingForm]);

  const handleGroupChange = (group) => {
    const keys = TAB_GROUPS[group];
    if (!keys?.includes(tab)) {
      setSearchParams({ tab: keys[0] });
    } else {
      setSearchParams({ tab });
    }
  };

  const refreshDiscCategories = async () => {
    const res = await discussionCategoriesApi.getCreateData();
    return res?.categories || [];
  };

  const openDiscModal = async () => {
    setDiscModalLoading(true);
    try {
      const res = await discussionsApi.getCreateData(id);
      setDiscCategories(res?.categories || []);
      discForm.resetFields();
      setDiscModal(true);
    } catch {
      message.error('خطا در بارگذاری فرم بحث');
    } finally {
      setDiscModalLoading(false);
    }
  };

  const openNoteModal = async (noteId = null) => {
    setNoteModalLoading(true);
    if (noteId) setNoteEditLoadingId(noteId);
    try {
      const res = await projectNotesApi.getCreateData(id);
      let employees = res?.employees || [];
      if (noteId) {
        const ed = await projectNotesApi.getEditData(noteId);
        const n = ed?.note || ed;
        const fromProject = (n.project?.project_members || []).map((m) => ({
          id: Number(m.id),
          name: m.name,
          image_url: m.image_url,
        }));
        const fromNoteUsers = (n.note_users || n.users || []).map((u) => ({
          id: Number(u.id ?? u.user_id),
          name: u.name,
          image_url: u.image_url,
        }));
        const byId = new Map();
        [...employees, ...fromProject, ...fromNoteUsers].forEach((e) => {
          if (!e?.id) return;
          byId.set(Number(e.id), { ...e, id: Number(e.id) });
        });
        employees = [...byId.values()];
        setNoteEmployees(employees);
        const userIds = (n.note_users || n.users || [])
          .map((u) => Number(u.id ?? u.user_id ?? u.pivot?.user_id))
          .filter((x) => x && !Number.isNaN(x));
        setEditNoteId(noteId);
        setNoteModal(true);
        queueMicrotask(() => {
          noteForm.setFieldsValue({
            title: n.title,
            type: Number(n.type ?? 0),
            details: stripHtml(n.details) || n.details,
            user_id: userIds,
            is_client_show: Number(n.is_client_show) === 1,
            ask_password: Number(n.ask_password) === 1,
          });
        });
      } else {
        setNoteEmployees(employees);
        setEditNoteId(null);
        noteForm.resetFields();
        noteForm.setFieldsValue({ type: 0 });
        setNoteModal(true);
      }
    } catch {
      message.error('خطا در بارگذاری فرم یادداشت');
    } finally {
      setNoteModalLoading(false);
      setNoteEditLoadingId(null);
    }
  };

  const openViewNote = async (note) => {
    const needsPwd = Number(note?.type) === 1 && Number(note?.ask_password) === 1;
    if (needsPwd) {
      setPwdNoteId(note.id);
      pwdForm.resetFields();
      setPwdModal(true);
      return;
    }
    setViewNoteLoading(true);
    setViewNoteModal(true);
    setViewNote(null);
    try {
      const res = await projectNotesApi.getById(note.id);
      setViewNote(res?.note || res?.data?.note || res);
    } catch {
      message.error('خطا در دریافت یادداشت');
      setViewNoteModal(false);
    } finally {
      setViewNoteLoading(false);
    }
  };

  const handleNotesExport = async () => {
    setNotesExporting(true);
    try {
      await projectNotesApi.exportList(id);
      message.success('خروجی اکسل در حال دانلود است');
    } catch (e) {
      message.error(e?.message || 'خطا در خروجی اکسل');
    } finally {
      setNotesExporting(false);
    }
  };

  const saveNote = async () => {
    const v = await noteForm.validateFields();
    setNoteSaving(true);
    try {
      const body = buildProjectNotePayload(v, id);
      if (editNoteId) await projectNotesApi.update(editNoteId, body);
      else await projectNotesApi.create(body);
      message.success('یادداشت ذخیره شد');
      setNoteModal(false);
      setEditNoteId(null);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, noteForm);
    } finally {
      setNoteSaving(false);
    }
  };

  const saveDiscussion = async () => {
    try {
      const v = await discForm.validateFields();
      setDiscSaving(true);
      try {
        await discussionsApi.create(buildDiscussionPayload(v, projectId));
        message.success('بحث ایجاد شد');
        setDiscModal(false);
        await reload({ silent: true });
      } catch (e) {
        show422Error(e, discForm);
      } finally {
        setDiscSaving(false);
      }
    } catch (e) {
      // خطای اعتبارسنجی Ant Design — نباید به runtime error تبدیل شود
      if (e?.errorFields) return;
      show422Error(e, discForm);
    }
  };

  const openMemberModal = async () => {
    setMemberModalLoading(true);
    try {
      const res = await projectMembersApi.getCreateData(id);
      setMemberOpts({
        employees: res?.employees || [],
        departments: res?.groups || res?.departments || [],
      });
      memberForm.resetFields();
      setMemberModal(true);
    } catch {
      message.error('خطا در بارگذاری فرم عضو');
    } finally {
      setMemberModalLoading(false);
    }
  };

  const saveMember = async () => {
    const user_id = memberForm.getFieldValue('user_id');
    if (!user_id?.length) {
      message.warning('حداقل یک کارمند انتخاب کنید');
      return;
    }
    setMemberSaving(true);
    try {
      await projectMembersApi.create(buildProjectMemberPayload(projectId, user_id));
      message.success('اعضا اضافه شدند');
      setMemberModal(false);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, noteForm);
    } finally {
      setMemberSaving(false);
    }
  };

  const saveMemberGroup = async () => {
    const groupId = memberForm.getFieldValue('group_id');
    if (!groupId) {
      message.warning('تیم را انتخاب کنید');
      return;
    }
    setMemberSaving(true);
    try {
      await projectMembersApi.saveGroup(buildProjectMemberGroupPayload(projectId, [groupId]));
      message.success('اعضای تیم اضافه شدند');
      setMemberModal(false);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, noteForm);
    } finally {
      setMemberSaving(false);
    }
  };

  const openMsModal = async (milestoneId = null) => {
    setMsModalLoading(true);
    if (milestoneId) setEditMsId(milestoneId);
    try {
      const res = await projectMilestonesApi.getCreateData(id);
      setMsMeta({ currencies: res?.currencies || [] });
      if (milestoneId) {
        const ed = await projectMilestonesApi.getEditData(milestoneId);
        const m = ed?.milestone || ed;
        msForm.setFieldsValue({
          milestone_title: m.milestone_title,
          summary: m.summary,
          cost: m.cost,
          status: m.status || 'incomplete',
          currency_id: m.currency_id,
          start_date: m.start_date ? toJalali(m.start_date) : undefined,
          end_date: m.end_date ? toJalali(m.end_date) : undefined,
        });
        setEditMsId(milestoneId);
      } else {
        msForm.resetFields();
        msForm.setFieldsValue({ status: 'incomplete' });
        setEditMsId(null);
      }
      setMsModal(true);
    } catch {
      message.error('خطا در بارگذاری فرم مرحله');
    } finally {
      setMsModalLoading(false);
    }
  };

  const saveMilestone = async () => {
    const v = await msForm.validateFields();
    setMsSaving(true);
    try {
      const body = buildProjectMilestonePayload(v, projectId);
      if (editMsId) {
        await projectMilestonesApi.update(editMsId, body);
      } else {
        await projectMilestonesApi.create(body);
      }
      message.success('مرحله ذخیره شد');
      setMsModal(false);
      setEditMsId(null);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, noteForm);
    } finally {
      setMsSaving(false);
    }
  };

  const saveRating = async () => {
    const v = await ratingForm.validateFields();
    setRatingSaving(true);
    try {
      const body = buildProjectRatingPayload(v, projectId);
      if (existingRating?.id) {
        await projectRatingsApi.update(existingRating.id, body);
        message.success('امتیاز به‌روزرسانی شد');
      } else {
        await projectRatingsApi.create(body);
        message.success('امتیاز ثبت شد');
      }
      setRatingEditing(false);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, noteForm);
    } finally {
      setRatingSaving(false);
    }
  };

  const submitReply = async () => {
    if (!replyText.trim() || !selectedDiscussionId) return;
    setReplySubmitting(true);
    try {
      await discussionRepliesApi.create(
        buildDiscussionReplyPayload(selectedDiscussionId, replyText),
      );
      setReplyText('');
      message.success('پاسخ ثبت شد');
      const res = await discussionsApi.getById(selectedDiscussionId);
      setDiscussionDetail(res?.discussion || res);
      await reload({ silent: true });
    } catch (e) {
      show422Error(e, noteForm);
    } finally {
      setReplySubmitting(false);
    }
  };

  const handleDeleteProject = () => {
    confirmWorkDelete({
      title: 'حذف پروژه',
      content: 'آیا از حذف این پروژه اطمینان دارید؟',
      onOk: async () => {
        await projectsApi.delete(id);
        message.success('پروژه حذف شد');
        navigate(returnToRef.current || '/work/projects', { state: buildGoBackState() });
      },
    });
  };

  const addBtn = (text, onClick, loadingBtn = false) => (
    <Button
      type="primary"
      icon={<PlusOutlined />}
      className={PANEL_PRIMARY_BTN_CLASS}
      onClick={onClick}
      loading={loadingBtn}
      style={{ fontFamily: 'CustomFont' }}
    >
      {text}
    </Button>
  );

  const financeTableProps = useMemo(
    () => ({
      className: 'work-panel-form',
      rowKey: 'id',
      pagination: false,
      scroll: { x: true },
      locale: { emptyText: <span style={{ fontFamily: 'CustomFont' }}>موردی نیست</span> },
    }),
    [],
  );

  const renderFinanceTab = (dataKey, columns, addConfig = null) => {
    const data = payload?.[dataKey] || [];
    return (
      <div className="work-panel-form" style={{ fontFamily: 'CustomFont' }}>
        {addConfig ? (
          <TabToolbar>
            {addBtn(addConfig.text, addConfig.onClick)}
          </TabToolbar>
        ) : null}
        <Table
          {...financeTableProps}
          dataSource={Array.isArray(data) ? data : []}
          columns={columns}
        />
      </div>
    );
  };

  const projectClientId =
    projectInfo?.client_id ?? projectInfo?.client?.id ?? null;

  const navigateProjectCreate = (path, tabKey, { clientParam } = {}) => {
    const params = new URLSearchParams();
    params.set('project_id', String(id));
    if (clientParam && projectClientId != null && projectClientId !== '') {
      params.set(clientParam, String(projectClientId));
    }
    navigate(`${path}?${params.toString()}`, {
      state: {
        returnTo: `/work/projects/${id}?tab=${tabKey}`,
        lockProject: true,
        lockClient: Boolean(clientParam && projectClientId),
      },
    });
  };

  const financeActionsColumn = (handlers = {}) =>
    workActionsColumn((_, r) => (
      <Space size={4}>
        {handlers.onView ? (
          <Tooltip title="مشاهده">
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => handlers.onView(r)} />
          </Tooltip>
        ) : null}
        {handlers.onEdit ? (
          <Tooltip title="ویرایش">
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handlers.onEdit(r)} />
          </Tooltip>
        ) : null}
        {handlers.onDelete ? (
          <Tooltip title="حذف">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handlers.onDelete(r)}
            />
          </Tooltip>
        ) : null}
      </Space>
    ), 130);

  const confirmFinanceDelete = (title, onOk) => {
    confirmWorkDelete({
      title,
      content: 'آیا از حذف این مورد اطمینان دارید؟',
      onOk: async () => {
        await onOk();
        message.success('حذف شد');
        await reload({ silent: true });
      },
    });
  };

  const renderTabBody = () => {
    if (tab === 'overview') {
      return <ProjectOverviewTab overview={payload?.overview} project={projectInfo} />;
    }

    if (tab === 'members') {
      const members = payload?.members || [];
      const projectAdminId = Number(
        payload?.project_admin ?? projectInfo?.project_admin ?? null,
      );
      const isMemberAdmin = (row) =>
        Boolean(row.is_project_admin) ||
        (projectAdminId && Number(row.user?.id) === projectAdminId);

      const saveHourlyRate = async (memberId, rate) => {
        setHourlyRateSavingId(memberId);
        try {
          await projectMembersApi.updateHourlyRate(memberId, rate);
          message.success('نرخ ساعتی به‌روزرسانی شد');
          await reload({ silent: true });
        } catch (e) {
          show422Error(e, noteForm);
        } finally {
          setHourlyRateSavingId(null);
        }
      };
      const assignAdmin = async (userId) => {
        if (!userId) return;
        setAdminAssigningUserId(userId);
        try {
          await projectsApi.assignProjectAdmin({ project_id: projectId, user_id: userId });
          message.success('مدیر پروژه تعیین شد');
          await loadProjectInfo();
          await reload({ silent: true });
        } catch (e) {
          show422Error(e, noteForm);
        } finally {
          setAdminAssigningUserId(null);
        }
      };
      const removeAdmin = async (userId) => {
        setAdminAssigningUserId(userId || 'remove');
        try {
          await projectsApi.assignProjectAdmin({
            project_id: projectId,
            user_id: null,
          });
          message.success('نقش مدیر پروژه برداشته شد');
          await loadProjectInfo();
          await reload({ silent: true });
        } catch (e) {
          show422Error(e, noteForm);
        } finally {
          setAdminAssigningUserId(null);
        }
      };
      return (
        <>
          <TabToolbar>{addBtn('افزودن عضو', openMemberModal, memberModalLoading)}</TabToolbar>
          {members.length === 0 ? (
            <Empty
              description={<span style={{ fontFamily: 'CustomFont' }}>عضوی ثبت نشده است</span>}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {members.map((row) => {
                const admin = isMemberAdmin(row);
                const user = row.user || {};
                const name = user.name || '—';
                const email = user.email || '';
                const avatarSrc = user.image_url || user.image;
                return (
                  <div
                    key={row.id}
                    className={`relative rounded-2xl border p-4 transition-shadow hover:shadow-md ${
                      admin
                        ? 'border-amber-300/80 bg-gradient-to-br from-amber-50 to-white dark:from-amber-950/30 dark:to-gray-800 dark:border-amber-700/50'
                        : 'border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700'
                    }`}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    <div className="flex items-start gap-3">
                      <Avatar
                        size={52}
                        src={avatarSrc}
                        className={admin ? 'ring-2 ring-amber-400 ring-offset-2' : ''}
                      >
                        {(name || '?').charAt(0)}
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {name}
                            </div>
                            {email ? (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                {email}
                              </div>
                            ) : null}
                          </div>
                          {admin ? (
                            <Tag
                              color="gold"
                              icon={<SafetyCertificateFilled />}
                              className="m-0 shrink-0"
                              style={{ fontFamily: 'CustomFont' }}
                            >
                              مدیر پروژه
                            </Tag>
                          ) : null}
                        </div>

                        <div className="mt-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                            نرخ ساعتی
                          </div>
                          <MemberHourlyRateCell
                            row={row}
                            saving={hourlyRateSavingId === row.id}
                            onSave={(rate) => saveHourlyRate(row.id, rate)}
                          />
                        </div>

                        <div className="mt-3 flex items-center justify-end gap-1 border-t border-gray-100 dark:border-gray-700 pt-3">
                          {admin ? (
                            <Popconfirm
                              title={label('حذف نقش مدیر پروژه؟')}
                              okText="بله"
                              cancelText="خیر"
                              onConfirm={() => removeAdmin(user.id)}
                            >
                              <Tooltip title="حذف ادمینی">
                                <Button
                                  type="text"
                                  danger
                                  icon={<SafetyCertificateOutlined />}
                                  loading={adminAssigningUserId === user.id}
                                >
                                  <span style={{ fontFamily: 'CustomFont' }}>حذف ادمین</span>
                                </Button>
                              </Tooltip>
                            </Popconfirm>
                          ) : (
                            <Tooltip title="تعیین به‌عنوان مدیر پروژه">
                              <Button
                                type="text"
                                icon={<SafetyCertificateOutlined className="text-amber-600" />}
                                loading={adminAssigningUserId === user.id}
                                onClick={() => assignAdmin(user.id)}
                              >
                                <span style={{ fontFamily: 'CustomFont' }}>مدیر کردن</span>
                              </Button>
                            </Tooltip>
                          )}
                          <Popconfirm
                            title={label('حذف عضو؟')}
                            onConfirm={async () => {
                              setMemberDeletingId(row.id);
                              try {
                                await projectMembersApi.delete(row.id);
                                await reload({ silent: true });
                              } finally {
                                setMemberDeletingId(null);
                              }
                            }}
                          >
                            <Tooltip title="حذف عضو">
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                loading={memberDeletingId === row.id}
                              />
                            </Tooltip>
                          </Popconfirm>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      );
    }

    if (tab === 'files') {
      const files = payload?.files || [];
      return (
        <WorkFilePanel
          files={files}
          onUpload={(fl) => projectFilesApi.upload(id, fl).then(() => reload({ silent: true }))}
          onDelete={(fid) => projectFilesApi.delete(fid).then(() => reload({ silent: true }))}
          getDownloadUrl={projectFilesApi.downloadUrl}
        />
      );
    }

    if (tab === 'milestones') {
      const milestones = payload?.milestones || [];
      return (
        <>
          <TabToolbar>{addBtn('افزودن مرحله', () => openMsModal(), msModalLoading)}</TabToolbar>
          <Table
            className="work-panel-form"
            rowKey="id"
            dataSource={milestones}
            pagination={false}
            scroll={{ x: true }}
            columns={[
              {
                title: label('عنوان'),
                dataIndex: 'milestone_title',
                render: (v) => cellFont(v),
              },
              {
                title: label('هزینه'),
                dataIndex: 'cost',
                render: (v) => cellFont(formatWorkPriceDisplay(v)),
              },
              {
                title: label('وضعیت'),
                width: 140,
                render: (_, row) => (
                  <Select
                    size="small"
                    value={row.status}
                    style={{ width: '100%', fontFamily: 'CustomFont' }}
                    loading={msStatusUpdatingId === row.id}
                    onChange={async (status) => {
                      setMsStatusUpdatingId(row.id);
                      try {
                        await projectMilestonesApi.updateStatus(row.id, status);
                        await reload({ silent: true });
                      } finally {
                        setMsStatusUpdatingId(null);
                      }
                    }}
                    options={[
                      { value: 'incomplete', label: 'ناتمام' },
                      { value: 'complete', label: 'تمام' },
                    ]}
                  />
                ),
              },
              {
                title: label('شروع'),
                dataIndex: 'start_date',
                render: (v) => cellDate(v),
              },
              {
                title: label('پایان'),
                dataIndex: 'end_date',
                render: (v) => cellDate(v),
              },
              workActionsColumn((_, row) => (
                <>
                  <Button
                    type="text"
                    icon={<EditOutlined />}
                    loading={msModalLoading && editMsId === row.id}
                    onClick={() => openMsModal(row.id)}
                  />
                  <Popconfirm
                    title={label('حذف؟')}
                    onConfirm={async () => {
                      setMsDeletingId(row.id);
                      try {
                        await projectMilestonesApi.delete(row.id);
                        await reload({ silent: true });
                      } finally {
                        setMsDeletingId(null);
                      }
                    }}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      loading={msDeletingId === row.id}
                    />
                  </Popconfirm>
                </>
              )),
            ]}
          />
        </>
      );
    }

    if (tab === 'invoices') {
      return renderFinanceTab(
        'invoices',
        [
          { title: label('شماره'), dataIndex: 'invoice_number', render: cellFont },
          {
            title: label('مبلغ کل'),
            render: (_, r) => cellFont(r.total_amount || formatWorkPriceDisplay(r.total)),
          },
          { title: label('وضعیت'), dataIndex: 'status', render: cellFont },
          { title: label('تاریخ صدور'), dataIndex: 'issue_on', render: cellDate },
          {
            title: label('مشتری'),
            render: (_, r) => cellFont(r.client?.name),
          },
          financeActionsColumn({
            onView: (r) =>
              navigateWithTrail(
                `/finance/invoices/${r.id}`,
                r.invoice_number || `فاکتور #${r.id}`,
                { returnTo: `/work/projects/${id}?tab=invoices` },
              ),
            onEdit: (r) =>
              navigate(`/finance/invoices/${r.id}/edit`, {
                state: { returnTo: `/work/projects/${id}?tab=invoices` },
              }),
            onDelete: (r) =>
              confirmFinanceDelete('حذف فاکتور', () => financeSettingsApi.deleteInvoice(r.id)),
          }),
        ],
        {
          text: 'افزودن فاکتور',
          onClick: () =>
            navigateProjectCreate('/finance/invoices/add', 'invoices', {
              clientParam: 'client_id',
            }),
        },
      );
    }

    if (tab === 'estimates') {
      return renderFinanceTab(
        'estimates',
        [
          { title: label('شماره'), dataIndex: 'estimate_number', render: cellFont },
          {
            title: label('مبلغ کل'),
            render: (_, r) => cellFont(r.total_amount || formatWorkPriceDisplay(r.total)),
          },
          { title: label('وضعیت'), dataIndex: 'status', render: cellFont },
          { title: label('اعتبار تا'), dataIndex: 'valid_date', render: cellDate },
          financeActionsColumn({
            onView: (r) =>
              navigate(`/finance/estimates/${r.id}/edit`, {
                state: { returnTo: `/work/projects/${id}?tab=estimates` },
              }),
            onEdit: (r) =>
              navigate(`/finance/estimates/${r.id}/edit`, {
                state: { returnTo: `/work/projects/${id}?tab=estimates` },
              }),
          }),
        ],
        {
          text: 'افزودن برآورد',
          onClick: () =>
            navigateProjectCreate('/finance/estimates/add', 'estimates', {
              clientParam: 'default_client',
            }),
        },
      );
    }

    if (tab === 'orders') {
      return renderFinanceTab(
        'orders',
        [
          { title: label('شماره'), dataIndex: 'order_number', render: cellFont },
          {
            title: label('مبلغ کل'),
            render: (_, r) => cellFont(formatWorkPriceDisplay(r.total)),
          },
          { title: label('وضعیت'), dataIndex: 'status', render: cellFont },
          { title: label('تاریخ'), dataIndex: 'order_date', render: cellDate },
          financeActionsColumn({
            onView: (r) =>
              navigateWithTrail(
                `/orders/${r.id}`,
                r.order_number || `سفارش #${r.id}`,
                { returnTo: `/work/projects/${id}?tab=orders` },
              ),
            onEdit: (r) =>
              navigate(`/orders/${r.id}/edit`, {
                state: { returnTo: `/work/projects/${id}?tab=orders` },
              }),
            onDelete: (r) =>
              confirmFinanceDelete('حذف سفارش', () => ordersApi.delete(r.id)),
          }),
        ],
        {
          text: 'افزودن سفارش',
          onClick: () =>
            navigateProjectCreate('/orders/add', 'orders', {
              clientParam: 'client_id',
            }),
        },
      );
    }

    if (tab === 'expenses') {
      return renderFinanceTab(
        'expenses',
        [
          { title: label('عنوان'), dataIndex: 'item_name', render: cellFont },
          {
            title: label('مبلغ'),
            render: (_, r) => cellFont(r.total_amount || formatWorkPriceDisplay(r.price)),
          },
          { title: label('وضعیت'), dataIndex: 'status', render: cellFont },
          { title: label('تاریخ خرید'), dataIndex: 'purchase_on', render: cellDate },
          financeActionsColumn({
            onView: (r) =>
              navigate(`/finance/expenses/${r.id}`, {
                state: { returnTo: `/work/projects/${id}?tab=expenses` },
              }),
            onEdit: (r) =>
              navigate(`/finance/expenses/${r.id}/edit`, {
                state: { returnTo: `/work/projects/${id}?tab=expenses` },
              }),
            onDelete: (r) =>
              confirmFinanceDelete('حذف هزینه', () => expensesApi.delete(r.id)),
          }),
        ],
        {
          text: 'افزودن هزینه',
          onClick: () => navigateProjectCreate('/finance/expenses/add', 'expenses'),
        },
      );
    }

    if (tab === 'payments') {
      return renderFinanceTab('payments', [
        { title: label('شناسه'), dataIndex: 'id', width: 80, render: cellFont },
        {
          title: label('مبلغ'),
          render: (_, r) =>
            cellFont(r.total_amount || formatWorkPriceDisplay(r.amount || r.default_currency_price)),
        },
        { title: label('وضعیت'), dataIndex: 'status', render: cellFont },
        { title: label('تاریخ پرداخت'), dataIndex: 'paid_date', render: cellDate },
        { title: label('درگاه'), dataIndex: 'gateway', render: cellFont },
      ]);
    }

    if (tab === 'tickets') {
      const tickets = payload?.tickets || [];
      const deleteTicket = (ticketId) => {
        confirmWorkDelete({
          title: 'حذف تیکت',
          content: 'آیا از حذف این تیکت اطمینان دارید؟',
          onOk: async () => {
            await ticketsApi.applyQuickAction('delete', [ticketId]);
            message.success('تیکت حذف شد');
            await reload({ silent: true });
          },
        });
      };
      return (
        <div className="work-panel-form" style={{ fontFamily: 'CustomFont' }}>
          <TabToolbar>
            {addBtn('افزودن تیکت', () =>
              navigate(`/tickets/add?project=${id}`, {
                state: {
                  projectId: Number(id),
                  lockProject: true,
                  returnTo: `/work/projects/${id}?tab=tickets`,
                },
              }),
            )}
          </TabToolbar>
          <Table
            {...financeTableProps}
            dataSource={tickets}
            columns={[
              {
                title: label('موضوع تیکت'),
                dataIndex: 'subject',
                render: (v, r) =>
                  cellFont(
                    <button
                      type="button"
                      className="text-teal-700 hover:underline bg-transparent border-0 p-0 cursor-pointer"
                      style={{ fontFamily: 'CustomFont' }}
                      onClick={() =>
                        navigateWithTrail(
                          `/tickets/${r.ticket_number || r.id}`,
                          r.subject || r.ticket_number || `تیکت #${r.id}`,
                          { returnTo: `/work/projects/${id}?tab=tickets` },
                        )
                      }
                    >
                      {v || '—'}
                    </button>,
                  ),
              },
              {
                title: label('نام درخواست‌کننده'),
                render: (_, r) =>
                  cellFont(r.client?.name || r.requester?.name || r.user?.name || '—'),
              },
              {
                title: label('درخواست شده در'),
                dataIndex: 'created_on',
                render: (v) => cellFont(formatWorkDateTime(v)),
              },
              {
                title: label('اولویت'),
                dataIndex: 'priority',
                render: (v) => cellFont(TICKET_PRIORITY_FA[v] || v || '—'),
              },
              {
                title: label('وضعیت'),
                dataIndex: 'status',
                render: (v) => cellFont(TICKET_STATUS_FA[v] || v || '—'),
              },
              workActionsColumn((_, r) => (
                <Space size={4}>
                  <Tooltip title="مشاهده / ویرایش">
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() =>
                        navigateWithTrail(
                          `/tickets/${r.ticket_number || r.id}`,
                          r.subject || r.ticket_number || `تیکت #${r.id}`,
                          { returnTo: `/work/projects/${id}?tab=tickets` },
                        )
                      }
                    />
                  </Tooltip>
                  <Tooltip title="ویرایش">
                    <Button
                      type="text"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() =>
                        navigateWithTrail(
                          `/tickets/${r.ticket_number || r.id}`,
                          r.subject || r.ticket_number || `تیکت #${r.id}`,
                          { returnTo: `/work/projects/${id}?tab=tickets` },
                        )
                      }
                    />
                  </Tooltip>
                  <Tooltip title="حذف">
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => deleteTicket(r.id)}
                    />
                  </Tooltip>
                </Space>
              ), 140),
            ]}
          />
        </div>
      );
    }

    if (tab === 'activity') {
      const activities = payload?.activities || [];
      return (
        <Table
          {...financeTableProps}
          dataSource={activities}
          locale={{
            emptyText: (
              <span style={{ fontFamily: 'CustomFont' }}>{PROJECT_ACTIVITY_EMPTY}</span>
            ),
          }}
          columns={[
            {
              title: label('نوع'),
              width: 64,
              align: 'center',
              render: (_, r) => activityKindIcon(getProjectActivityKind(r.activity)),
            },
            {
              title: label('فعالیت'),
              dataIndex: 'activity',
              render: (v) => (
                <span style={{ fontFamily: 'CustomFont' }}>
                  {translateProjectActivity(v)}
                </span>
              ),
            },
            {
              title: label('تاریخ و ساعت'),
              width: 180,
              render: (_, r) =>
                cellFont(
                  formatWorkDateTime(
                    r.created_at || r.created_on || r.updated_at || r.date,
                  ),
                ),
            },
          ]}
        />
      );
    }

    if (tab === 'discussion') {
      const discussions = payload?.discussion || payload?.discussions || [];
      const bestAnswerId = Number(discussionDetail?.best_answer_id);
      return (
        <div className="work-panel-form" style={{ fontFamily: 'CustomFont' }}>
          <TabToolbar>
            {addBtn('افزودن بحث', openDiscModal, discModalLoading)}
            <Button
              icon={<SettingOutlined />}
              onClick={() => setDiscCatModal(true)}
              style={{ fontFamily: 'CustomFont' }}
            >
              دسته‌های بحث
            </Button>
          </TabToolbar>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={9}>
              <div className="work-scroll-pane space-y-2 pr-1" style={{ maxHeight: '70vh' }}>
                {discussions.length === 0 ? (
                  <Empty description={<span style={{ fontFamily: 'CustomFont' }}>بحثی نیست</span>} />
                ) : (
                  discussions.map((d) => {
                    const selected = selectedDiscussionId === d.id;
                    const created =
                      formatWorkDate(d.created_at) !== '—'
                        ? formatWorkDate(d.created_at)
                        : formatWorkDate(d.last_reply_at);
                    return (
                      <button
                        type="button"
                        key={d.id}
                        onClick={() => setSelectedDiscussionId(d.id)}
                        className={`w-full text-right rounded-xl border p-3 transition-all ${
                          selected
                            ? 'border-teal-400 bg-teal-50 shadow-sm dark:bg-teal-900/20 dark:border-teal-600'
                            : 'border-gray-200 bg-white hover:border-teal-200 dark:bg-gray-800 dark:border-gray-700'
                        }`}
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                              {d.title || d.subject || '—'}
                            </div>
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              {d.category ? (
                                <Tag color={d.category.color || 'blue'} className="m-0" style={{ fontFamily: 'CustomFont' }}>
                                  {d.category.name}
                                </Tag>
                              ) : null}
                              {d.best_answer_id ? (
                                <Tag color="success" className="m-0" style={{ fontFamily: 'CustomFont' }}>
                                  بهترین پاسخ دارد
                                </Tag>
                              ) : null}
                            </div>
                            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-1">
                              <span>{d.replies_count ?? d.replies?.length ?? 0} پاسخ</span>
                              {created !== '—' ? <span>{created}</span> : null}
                            </div>
                          </div>
                          <Popconfirm
                            title={label('حذف بحث؟')}
                            onConfirm={async (e) => {
                              e?.stopPropagation?.();
                              setDiscDeletingId(d.id);
                              try {
                                await discussionsApi.delete(d.id);
                                if (selectedDiscussionId === d.id) {
                                  setSelectedDiscussionId(null);
                                  setDiscussionDetail(null);
                                }
                                await reload({ silent: true });
                              } finally {
                                setDiscDeletingId(null);
                              }
                            }}
                          >
                            <Button
                              type="text"
                              size="small"
                              danger
                              icon={<DeleteOutlined />}
                              loading={discDeletingId === d.id}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </Popconfirm>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </Col>
            <Col xs={24} lg={15}>
              <Spin spinning={discussionDetailLoading} wrapperClassName="work-discussion-spin">
                {discussionDetail ? (
                  <div
                    className="work-scroll-contain work-discussion-panel rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
                    style={{
                      display: 'grid',
                      gridTemplateRows: 'auto minmax(0, 1fr) auto',
                      height: 560,
                      maxHeight: '70vh',
                    }}
                  >
                    <div className="work-discussion-header px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                      <div
                        className="font-semibold text-lg text-gray-900 dark:text-gray-100"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {discussionDetail.title}
                      </div>
                      <div
                        className="mt-1 flex flex-wrap gap-2 text-xs text-gray-500"
                        style={{ fontFamily: 'CustomFont' }}
                      >
                        {discussionDetail.category ? (
                          <Tag
                            color={discussionDetail.category.color || 'blue'}
                            style={{ fontFamily: 'CustomFont' }}
                          >
                            {discussionDetail.category.name}
                          </Tag>
                        ) : null}
                        {formatWorkDate(discussionDetail.created_at) !== '—' ? (
                          <span>ایجاد: {formatWorkDate(discussionDetail.created_at)}</span>
                        ) : null}
                        {formatWorkDate(discussionDetail.last_reply_at) !== '—' ? (
                          <span>آخرین پاسخ: {formatWorkDate(discussionDetail.last_reply_at)}</span>
                        ) : null}
                      </div>
                      {discussionDetail.description || discussionDetail.body ? (
                        <p
                          className="mt-3 mb-0 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap"
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          {stripHtml(discussionDetail.description || discussionDetail.body)}
                        </p>
                      ) : null}
                    </div>

                    <div className="work-scroll-pane p-4 space-y-3">
                      {(discussionDetail.replies || []).length === 0 ? (
                        <Empty
                          description={
                            <span style={{ fontFamily: 'CustomFont' }}>پاسخی نیست</span>
                          }
                        />
                      ) : (
                        (discussionDetail.replies || []).map((r) => {
                          const isBest = bestAnswerId && Number(r.id) === bestAnswerId;
                          const replyBody = stripHtml(
                            r.body || r.description || r.reply || '',
                          );
                          return (
                            <div
                              key={r.id}
                              className={`rounded-xl border p-3 ${
                                isBest
                                  ? 'border-green-400 bg-green-50 dark:bg-green-950/30 dark:border-green-600'
                                  : 'border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/30'
                              }`}
                              style={{ fontFamily: 'CustomFont' }}
                            >
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex items-center gap-2 min-w-0">
                                  <Avatar size={32} src={r.user?.image_url}>
                                    {(r.user?.name || '?').charAt(0)}
                                  </Avatar>
                                  <div className="min-w-0">
                                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                      {r.user?.name || '—'}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {formatWorkDate(r.created_at)}
                                    </div>
                                  </div>
                                </div>
                                {isBest ? (
                                  <Tag
                                    color="success"
                                    className="m-0"
                                    style={{ fontFamily: 'CustomFont' }}
                                  >
                                    بهترین پاسخ
                                  </Tag>
                                ) : null}
                              </div>
                              <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-6">
                                {replyBody || '—'}
                              </div>
                              <div className="mt-3 flex flex-wrap gap-1 justify-end">
                                {!isBest ? (
                                  <Button
                                    size="small"
                                    loading={bestAnswerLoadingId === r.id}
                                    style={{ fontFamily: 'CustomFont' }}
                                    onClick={async () => {
                                      setBestAnswerLoadingId(r.id);
                                      try {
                                        await discussionRepliesApi.setBestAnswer({
                                          reply_id: r.id,
                                          type: 'set',
                                        });
                                        const res = await discussionsApi.getById(
                                          selectedDiscussionId,
                                        );
                                        setDiscussionDetail(res?.discussion || res);
                                        await reload({ silent: true });
                                      } finally {
                                        setBestAnswerLoadingId(null);
                                      }
                                    }}
                                  >
                                    انتخاب بهترین پاسخ
                                  </Button>
                                ) : null}
                                <Popconfirm
                                  title={label('حذف پاسخ؟')}
                                  onConfirm={async () => {
                                    setReplyDeletingId(r.id);
                                    try {
                                      await discussionRepliesApi.delete(r.id);
                                      const res = await discussionsApi.getById(
                                        selectedDiscussionId,
                                      );
                                      setDiscussionDetail(res?.discussion || res);
                                      await reload({ silent: true });
                                    } finally {
                                      setReplyDeletingId(null);
                                    }
                                  }}
                                >
                                  <Button
                                    size="small"
                                    danger
                                    loading={replyDeletingId === r.id}
                                    style={{ fontFamily: 'CustomFont' }}
                                  >
                                    حذف
                                  </Button>
                                </Popconfirm>
                                {(r.files || []).map((f) => (
                                  <Button
                                    key={f.id}
                                    size="small"
                                    style={{ fontFamily: 'CustomFont' }}
                                    onClick={() =>
                                      window.open(
                                        discussionFilesApi.downloadUrl(f.id),
                                        '_blank',
                                      )
                                    }
                                  >
                                    دانلود فایل
                                  </Button>
                                ))}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>

                    <div className="work-discussion-composer p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                      <div className="flex flex-col sm:flex-row gap-2">
                        <TextArea
                          rows={2}
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="پاسخ خود را بنویسید..."
                          style={{ fontFamily: 'CustomFont' }}
                          className="flex-1"
                        />
                        <Button
                          type="primary"
                          className={PANEL_PRIMARY_BTN_CLASS}
                          loading={replySubmitting}
                          onClick={submitReply}
                          style={{ fontFamily: 'CustomFont' }}
                        >
                          ارسال پاسخ
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full min-h-[240px] flex items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-600">
                    <p className="text-gray-500 m-0" style={{ fontFamily: 'CustomFont' }}>
                      یک بحث را از فهرست انتخاب کنید
                    </p>
                  </div>
                )}
              </Spin>
            </Col>
          </Row>
        </div>
      );
    }

    if (tab === 'notes') {
      const notes = payload?.notes || [];
      return (
        <div className="work-panel-form" style={{ fontFamily: 'CustomFont' }}>
          <TabToolbar>
            {addBtn('افزودن یادداشت', () => openNoteModal(), noteModalLoading)}
            <Button
              icon={<FileExcelOutlined />}
              loading={notesExporting}
              onClick={handleNotesExport}
              style={{ fontFamily: 'CustomFont' }}
            >
              خروجی اکسل
            </Button>
          </TabToolbar>
          <List
            dataSource={notes}
            locale={{ emptyText: <span style={{ fontFamily: 'CustomFont' }}>یادداشتی نیست</span> }}
            renderItem={(n) => (
              <List.Item
                actions={[
                  <Button
                    key="v"
                    size="small"
                    icon={<EyeOutlined />}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => openViewNote(n)}
                  >
                    مشاهده
                  </Button>,
                  <Button
                    key="e"
                    size="small"
                    loading={noteEditLoadingId === n.id}
                    style={{ fontFamily: 'CustomFont' }}
                    onClick={() => openNoteModal(n.id)}
                  >
                    ویرایش
                  </Button>,
                  <Popconfirm
                    key="d"
                    title={label('حذف؟')}
                    onConfirm={async () => {
                      setNoteDeletingId(n.id);
                      try {
                        await projectNotesApi.delete(n.id);
                        await reload({ silent: true });
                      } finally {
                        setNoteDeletingId(null);
                      }
                    }}
                  >
                    <Button
                      size="small"
                      danger
                      loading={noteDeletingId === n.id}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      حذف
                    </Button>
                  </Popconfirm>,
                ]}
              >
                <div style={{ fontFamily: 'CustomFont' }}>
                  <div className="font-medium">{n.title || '—'}</div>
                  <div className="mt-1 flex flex-wrap gap-2 items-center">
                    <Tag color={Number(n.type) === 1 ? 'purple' : 'blue'}>
                      {Number(n.type) === 1 ? 'خصوصی' : 'عمومی'}
                    </Tag>
                    {Number(n.type) === 1 && Number(n.ask_password) === 1 ? (
                      <Tag color="orange">نیاز به رمز</Tag>
                    ) : null}
                  </div>
                </div>
              </List.Item>
            )}
          />
        </div>
      );
    }

    if (tab === 'rating') {
      const showForm = ratingEditing || !existingRating;
      return (
        <div className="work-panel-form" style={{ fontFamily: 'CustomFont' }}>
          {existingRating && !ratingEditing ? (
            <Card className="shadow-sm border-0 mb-4">
              <div className="flex flex-wrap items-start gap-4">
                <Avatar src={existingRating.user?.image_url} size={48}>
                  {(resolveUserName(existingRating.user_id || existingRating.added_by, existingRating.user) || '?')[0]}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold mb-1" style={{ fontFamily: 'CustomFont' }}>
                    {resolveUserName(existingRating.user_id || existingRating.added_by, existingRating.user)}
                  </div>
                  <Rate disabled value={Number(existingRating.rating)} />
                  <p className="mt-2 mb-0 text-gray-600 whitespace-pre-wrap" style={{ fontFamily: 'CustomFont' }}>
                    {existingRating.comment}
                  </p>
                </div>
                <Space>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => setRatingEditing(true)}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    ویرایش
                  </Button>
                  <Button
                    danger
                    loading={ratingDeleting}
                    onClick={() =>
                      confirmWorkDelete({
                        title: 'حذف امتیاز',
                        content: 'آیا از حذف این امتیاز اطمینان دارید؟',
                        onOk: async () => {
                          setRatingDeleting(true);
                          try {
                            await projectRatingsApi.delete(existingRating.id);
                            message.success('امتیاز حذف شد');
                            ratingForm.resetFields();
                            setRatingEditing(true);
                            await reload({ silent: true });
                          } finally {
                            setRatingDeleting(false);
                          }
                        },
                      })
                    }
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    حذف
                  </Button>
                </Space>
              </div>
            </Card>
          ) : null}

          {showForm ? (
            <Card className="shadow-sm border-0" title={label(existingRating ? 'ویرایش امتیاز' : 'ثبت امتیاز')}>
              <Form form={ratingForm} layout="vertical" onFinish={saveRating}>
                <Form.Item
                  name="rating"
                  label={label('امتیاز')}
                  rules={[{ required: true, message: 'امتیاز را انتخاب کنید' }]}
                >
                  <Rate />
                </Form.Item>
                <Form.Item
                  name="comment"
                  label={label('نظر')}
                  rules={[{ required: true, message: 'نظر الزامی است' }]}
                >
                  <TextArea rows={3} style={{ fontFamily: 'CustomFont' }} />
                </Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={ratingSaving}
                    className={PANEL_PRIMARY_BTN_CLASS}
                    style={{ fontFamily: 'CustomFont' }}
                  >
                    {existingRating ? 'به‌روزرسانی' : 'ثبت امتیاز'}
                  </Button>
                  {existingRating ? (
                    <Button
                      onClick={() => {
                        setRatingEditing(false);
                        ratingForm.setFieldsValue({
                          rating: Number(existingRating.rating),
                          comment: existingRating.comment,
                        });
                      }}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      انصراف
                    </Button>
                  ) : null}
                </Space>
              </Form>
            </Card>
          ) : null}
        </div>
      );
    }

    return (
      <p className="text-gray-500" style={{ fontFamily: 'CustomFont' }}>
        داده‌ای نیست
      </p>
    );
  };

  return (
    <WorkDetailPage
      title={projectInfo?.project_name || 'جزئیات پروژه'}
      subtitle={projectInfo?.project_short_code}
      icon={ProjectOutlined}
      iconClassName="bg-teal-50 dark:bg-teal-900/20"
      iconColorClass="text-brand-primary"
      backPath="/work/projects"
      onBack={goBack}
      breadcrumbTrail={trail}
      onBreadcrumbNavigate={goToCrumb}
      loading={loading && !projectInfo}
      headerExtra={
        <Space wrap style={{ fontFamily: 'CustomFont' }}>
          <PermissionGate permission="edit_project">
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                navigateWithTrail(
                  `/work/projects/${id}/edit`,
                  projectInfo?.project_name
                    ? `ویرایش ${projectInfo.project_name}`
                    : `ویرایش پروژه #${id}`,
                  {
                    returnTo: returnToRef.current || '/work/projects',
                    ...buildGoBackState(),
                  },
                )
              }
              style={{ fontFamily: 'CustomFont' }}
            >
              ویرایش
            </Button>
          </PermissionGate>
          <PermissionGate permission="delete_project">
            <Button danger onClick={handleDeleteProject} style={{ fontFamily: 'CustomFont' }}>
              حذف
            </Button>
          </PermissionGate>
        </Space>
      }
    >
      {projectInfo ? (
        <Card className="shadow-sm border-0 mb-4 work-panel-form" style={{ fontFamily: 'CustomFont' }}>
          <Row gutter={[16, 8]}>
            <Col xs={24} sm={12}>
              <strong style={{ fontFamily: 'CustomFont' }}>نام: </strong>
              <span style={{ fontFamily: 'CustomFont' }}>
                {projectInfo.project_name || '—'}
              </span>
            </Col>
            <Col xs={24} sm={12}>
              <strong style={{ fontFamily: 'CustomFont' }}>مشتری: </strong>
              <span style={{ fontFamily: 'CustomFont' }}>
                {projectInfo.client?.name || '—'}
              </span>
            </Col>
            <Col xs={24}>
              <strong style={{ fontFamily: 'CustomFont' }}>خلاصه: </strong>
              <span style={{ fontFamily: 'CustomFont' }} className="whitespace-pre-wrap">
                {projectInfo.project_summary || '—'}
              </span>
            </Col>
          </Row>
          <div className="flex flex-wrap gap-2 items-center mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <Tag style={{ fontFamily: 'CustomFont' }}>{projectInfo.project_short_code}</Tag>
            <Tag color="blue" style={{ fontFamily: 'CustomFont' }}>{projectInfo.status}</Tag>
            <Progress
              percent={projectInfo.completion_percent || 0}
              className="max-w-xs flex-1 min-w-[120px]"
            />
          </div>
        </Card>
      ) : null}

      <Card className="shadow-sm border-0 work-panel-form" style={{ fontFamily: 'CustomFont' }}>
        <div className="mb-4">
          <Segmented
            value={tabGroup}
            onChange={handleGroupChange}
            options={Object.keys(TAB_GROUPS).map((g) => ({
              value: g,
              label: <span style={{ fontFamily: 'CustomFont' }}>{GROUP_LABELS[g]}</span>,
            }))}
            className="mb-3"
          />
          <Tabs
            activeKey={tab}
            onChange={(k) => setSearchParams({ tab: k })}
            items={activeGroupTabs.map((k) => ({
              key: k,
              label: label(TAB_LABELS[k] || k),
            }))}
          />
        </div>
        <div className="pt-2">
          {tabLoading ? (
            <WorkProjectTabSkeleton tab={tab} />
          ) : (
            <Spin spinning={refreshing}>{renderTabBody()}</Spin>
          )}
        </div>
      </Card>

      <Modal
        open={noteModal}
        title={label(editNoteId ? 'ویرایش یادداشت' : 'افزودن یادداشت')}
        onCancel={() => {
          setNoteModal(false);
          setEditNoteId(null);
        }}
        onOk={saveNote}
        confirmLoading={noteSaving}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
        width={640}
      >
        <Form form={noteForm} layout="vertical" className="work-panel-form">
          <Form.Item name="title" label={label('عنوان')} rules={[{ required: true }]}>
            <Input style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="type" label={label('نوع')} rules={[{ required: true }]}>
            <Select style={{ fontFamily: 'CustomFont' }}>
              <Option value={0}>عمومی</Option>
              <Option value={1}>خصوصی</Option>
            </Select>
          </Form.Item>
          {Number(noteType) === 1 ? (
            <>
              <Form.Item
                name="user_id"
                label={label('کارمندان')}
                rules={[{ required: true, message: 'انتخاب کنید' }]}
              >
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  style={{ fontFamily: 'CustomFont' }}
                  options={(noteEmployees || []).map((e) => ({
                    value: Number(e.id),
                    label: e.name,
                  }))}
                />
              </Form.Item>
              <Form.Item name="is_client_show" valuePropName="checked" initialValue={false}>
                <WorkCheckbox>نمایش به مشتری</WorkCheckbox>
              </Form.Item>
              <Form.Item name="ask_password" valuePropName="checked" initialValue={false}>
                <WorkCheckbox>نیاز به رمز</WorkCheckbox>
              </Form.Item>
            </>
          ) : null}
          <Form.Item name="details" label={label('جزئیات')} rules={[{ required: true }]}>
            <TextArea rows={4} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={pwdModal}
        title={label('ورود رمز یادداشت')}
        onCancel={() => {
          setPwdModal(false);
          setPwdNoteId(null);
        }}
        onOk={async () => {
          try {
            const v = await pwdForm.validateFields();
            setPwdChecking(true);
            const body = { password: v.password };
            if (pwdNoteId != null) body.note_id = Number(pwdNoteId);
            const res = await projectNotesApi.checkPassword(body);
            if (res?.status === 'success' || res?.success) {
              message.success(res?.message || 'رمز صحیح است');
              const noteIdToOpen = pwdNoteId;
              setPwdModal(false);
              setPwdNoteId(null);
              pwdForm.resetFields();
              if (noteIdToOpen) {
                setViewNoteLoading(true);
                setViewNoteModal(true);
                setViewNote(null);
                try {
                  const noteRes = await projectNotesApi.getById(noteIdToOpen);
                  setViewNote(noteRes?.note || noteRes?.data?.note || noteRes);
                } catch {
                  message.error('خطا در دریافت یادداشت');
                  setViewNoteModal(false);
                } finally {
                  setViewNoteLoading(false);
                }
              }
            } else {
              message.error(res?.message || 'رمز نادرست است');
            }
          } catch (e) {
            if (e?.errorFields) return;
            const errMsg = e?.response?.data?.message || e?.message;
            if (errMsg) message.error(errMsg);
            else show422Error(e, pwdForm);
          } finally {
            setPwdChecking(false);
          }
        }}
        confirmLoading={pwdChecking}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
      >
        <Form form={pwdForm} layout="vertical" className="work-panel-form">
          <Form.Item name="password" label={label('رمز')} rules={[{ required: true, message: 'رمز الزامی است' }]}>
            <Input.Password style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={viewNoteModal}
        title={label('مشاهده یادداشت')}
        onCancel={() => {
          setViewNoteModal(false);
          setViewNote(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setViewNoteModal(false);
              setViewNote(null);
            }}
            style={{ fontFamily: 'CustomFont' }}
          >
            بستن
          </Button>,
        ]}
        width={640}
        destroyOnClose
      >
        {viewNoteLoading ? (
          <div className="py-8 text-center" style={{ fontFamily: 'CustomFont' }}>
            در حال بارگذاری...
          </div>
        ) : (
          <Descriptions
            bordered
            column={1}
            size="small"
            labelStyle={{ fontFamily: 'CustomFont' }}
            contentStyle={{ fontFamily: 'CustomFont' }}
          >
            <Descriptions.Item label={label('عنوان')}>
              {viewNote?.title || '—'}
            </Descriptions.Item>
            <Descriptions.Item label={label('نوع')}>
              {Number(viewNote?.type) === 1 ? 'خصوصی' : 'عمومی'}
            </Descriptions.Item>
            <Descriptions.Item label={label('جزئیات')}>
              <span className="whitespace-pre-wrap">
                {stripHtml(viewNote?.details) || viewNote?.details || '—'}
              </span>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <Modal
        open={discModal}
        title={label('افزودن بحث')}
        onCancel={() => setDiscModal(false)}
        onOk={saveDiscussion}
        confirmLoading={discSaving}
        okText="تایید"
        cancelText="لغو"
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
        width={560}
      >
        <Form form={discForm} layout="vertical" className="work-panel-form" style={{ fontFamily: 'CustomFont' }}>
          <Form.Item name="title" label={label('عنوان')} rules={[{ required: true, message: 'عنوان الزامی است' }]}>
            <Input style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item
            name="discussion_category"
            label={label('دسته بحث')}
            rules={[{ required: true, message: 'دسته بحث الزامی است' }]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="انتخاب دسته"
              style={{ fontFamily: 'CustomFont', width: '100%' }}
              options={discCategories.map((c) => ({
                value: Number(c.id),
                label: c.name || c.category_name,
              }))}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div className="border-t border-gray-100 p-2">
                    <Button
                      type="link"
                      block
                      icon={<SettingOutlined />}
                      onClick={() => setDiscCatModal(true)}
                      style={{ fontFamily: 'CustomFont' }}
                    >
                      مدیریت دسته‌های بحث
                    </Button>
                  </div>
                </>
              )}
            />
          </Form.Item>
          <Form.Item name="description" label={label('توضیحات')} rules={[{ required: true, message: 'توضیحات الزامی است' }]}>
            <TextArea rows={4} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
        </Form>
      </Modal>

      <WorkManageEntityModal
        open={discCatModal}
        onClose={async () => {
          setDiscCatModal(false);
          const cats = await refreshDiscCategories();
          setDiscCategories(cats);
        }}
        title="دسته‌بندی بحث"
        nameField="category_name"
        nameLabel="نام دسته"
        loadItems={refreshDiscCategories}
        onCreate={(body) => discussionCategoriesApi.create(body)}
        onUpdate={(cid, body) => discussionCategoriesApi.update(cid, body)}
        onDelete={(cid) => discussionCategoriesApi.delete(cid)}
        getItemLabel={(i) => i.name || i.category_name}
        buildCreatePayload={(v) => buildDiscussionCategoryPayload(v)}
        buildUpdatePayload={(cid, name) => buildDiscussionCategoryPayload({ category_name: name })}
        extraFormFields={<WorkColorPickerField name="color" label="رنگ" />}
      />

      <Modal
        open={memberModal}
        title={label('افزودن عضو')}
        onCancel={() => setMemberModal(false)}
        onOk={saveMember}
        confirmLoading={memberSaving}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
        footer={[
          <Button
            key="cancel"
            onClick={() => setMemberModal(false)}
            style={{ fontFamily: 'CustomFont' }}
          >
            لغو
          </Button>,
          <Button
            key="group"
            loading={memberSaving}
            onClick={saveMemberGroup}
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن تیم
          </Button>,
          <Button
            key="ok"
            type="primary"
            className={PANEL_PRIMARY_BTN_CLASS}
            loading={memberSaving}
            onClick={saveMember}
            style={{ fontFamily: 'CustomFont' }}
          >
            افزودن کارمند
          </Button>,
        ]}
      >
        <Form form={memberForm} layout="vertical" className="work-panel-form">
          <Form.Item name="user_id" label={label('کارمندان')}>
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              options={(memberOpts.employees || []).map((e) => ({ value: e.id, label: e.name }))}
            />
          </Form.Item>
          <Form.Item name="group_id" label={label('تیم')}>
            <Select
              allowClear
              optionFilterProp="label"
              placeholder="انتخاب تیم"
              options={(memberOpts.departments || []).map((d) => ({
                value: d.id,
                label: d.team_name || d.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={msModal}
        title={label(editMsId ? 'ویرایش مرحله' : 'افزودن مرحله')}
        onCancel={() => {
          setMsModal(false);
          setEditMsId(null);
        }}
        onOk={saveMilestone}
        confirmLoading={msSaving}
        okButtonProps={{ className: PANEL_PRIMARY_BTN_CLASS }}
        destroyOnClose
        width={640}
      >
        <Form form={msForm} layout="vertical" className="work-panel-form">
          <Form.Item name="milestone_title" label={label('عنوان')} rules={[{ required: true }]}>
            <Input style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Form.Item name="summary" label={label('خلاصه')}>
            <TextArea rows={2} style={{ fontFamily: 'CustomFont' }} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="cost" label={label('هزینه')}>
                <WorkPriceInput />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="currency_id" label={label('ارز')}>
                <Select allowClear style={{ fontFamily: 'CustomFont' }} optionFilterProp="label">
                  {msMeta.currencies.map((c) => (
                    <Option key={c.id} value={c.id} label={formatCurrencyLabel(c)}>
                      {formatCurrencyLabel(c)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="start_date" label={label('تاریخ شروع')}>
                <PersianDatePicker className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="end_date" label={label('تاریخ پایان')}>
                <PersianDatePicker className="w-full" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="status" label={label('وضعیت')}>
                <Select>
                  <Option value="incomplete">ناتمام</Option>
                  <Option value="complete">تمام</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </WorkDetailPage>
  );
};

export default ViewProject;
